import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { buildErrorMessage, getCommentType, projectFilesRootGuid } from '@constants'
import { getElementDocuments, getSupportsOfElement } from '@domainUtils'
import { mapValueKey } from '@editorUtils'
import { useSlabBeamToAssembly } from '@hooks'
import { VerticalTransmitterList } from '@structuralPlanningComponents'
import { AxiosError } from 'axios'
import { filter, find, findIndex, reject } from 'lodash-es'
import { Box, Stack, useTheme } from '@mui/material'
import useResultsSectionOpen from '@editor/pages/Results2/hooks/useResultsSectionOpen'
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query'
import { EmptyState } from '@ui2/feedback'
import { Section } from '@ui2/structure'
import { Form2 } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import {
  useCommentsStore,
  useModelStore,
  useEditorViewStore,
  useResultsStore,
  useTransmissionStore,
} from '@editorStores'
import {
  useResultsSelection,
  useAllDomains,
  useElementLabel,
  useElementCrossSectionAssignment,
  useElementType,
  useElementTypes,
  useProjectFiles,
  useWallIdToAssembly,
  useInnerForcesEnvelopeInvalidation,
} from '@editorHooks'
import { postCalcPositionChecks, getModel } from '@queries'
import {
  saveCrossSectionsOnElement,
  savePositionGrouping,
  saveSingleMemberCheckSettings,
} from '@mutations'
import { useEnhanceLoadsWithDomain } from '../../hooks/useEnhanceLoadsWithDomain'
import useResults2DefaultLoadTab from '../../hooks/useResults2DefaultLoadTab'
import { collectFokusGuidsForElement } from '../../hooks/useResults2FokusEffect'
import useSyncLinkedPlanCommentText from '../../hooks/useSyncLinkedPlanCommentText'
import Results2PaneHeader from '../Results2PaneHeader'
import Results2LoadResults from './Results2LoadResults'
import { BottomActionBar } from './components/BottomActionBar'
import { CharacteristicSupportReactionsSection } from './components/CharacteristicSupportReactionsSection'
import {
  CheckSettingsRecalculation,
  checkSettingsRecalculationMutationFilter,
} from './components/CheckSettingsRecalculation'
import CommentsSection from './components/CommentsSection'
import { DesignFormDirtyBlocker } from './components/DesignFormDirtyBlocker'
import { DocumentsTab } from './components/DocumentsTab'
import ElementStatusHeader from './components/ElementStatusHeader'
import { FooterTable } from './components/FooterTable'
import { GuardedResults2DetailTabs } from './components/GuardedResults2DetailTabs'
import { InnerForcesSection } from './components/InnerForcesSection'
import { LastenTable } from './components/LastenTable'
import { MaterialSection } from './components/MaterialSection'
import { MemberChecksSection } from './components/MemberChecksSection'
import Results2CheckSettingsTabs from './components/Results2CheckSettingsTabs'
import { SupportsGrid } from './components/SupportsGrid'
import SyncCrossSectionWithScene from './components/SyncCrossSectionWithScene'
import SystemSection from './components/SystemSection'
import { ELEMENT_TYPES_WITH_INNER_FORCES_PLOT } from './constants'
import { results2DesignFormValidationSchema } from './designFormSchema'
import type { DesignFormRootValues, DetailPaneTabId, Results2DetailPaneProps } from './types'
import useReconcilePlanComment from './useReconcilePlanComment'
import {
  beamLikeGuidsForCalcPositionChecks,
  buildDefaultDesignFormValues,
  buildMemberCheckSettingSupportsContext,
  commentTextsForPositionGroupingBundle,
  exportedFlagForPositionGroupingRepresentativeOnly,
  resolveExportedForPositionGroupingSave,
  hydrateCrossSectionForCalcElementChecksRequest,
  resolveResults2DetailPaneCrossSection,
  withMemberCheckSettingsReferenceData,
  matchesRelativePosition,
} from './utils'

const DetailPaneExportedToggle = ({
  elementLabel,
  elementId,
  elementSubtitle,
  calcElementChecksPending,
  disableForm = false,
  isChildElement = false,
}: {
  elementLabel?: string
  elementId?: string
  elementSubtitle: string
  calcElementChecksPending: boolean
  disableForm?: boolean
  isChildElement?: boolean
}): ReactElement => {
  return (
    <Controller
      name="elements.0.exported"
      render={({ field }) => (
        <ElementStatusHeader
          elementId={elementLabel ?? elementId ?? '—'}
          geometryGuid={elementId}
          subtitle={elementSubtitle}
          exported={!!field.value}
          onExportedChange={field.onChange}
          calcChecksPending={calcElementChecksPending}
          disableForm={disableForm}
          isChildElement={isChildElement}
        />
      )}
    />
  )
}

const Results2DetailPane = ({
  elementId: selectedGuid,
  elementLabel,
  elementSubtitle = '01.OG · Sturz · Nadelholz C24',
  accepted,
  isChildElement = false,
  positionGrouping,
  positionGroupingType,
  disableForm = false,
}: Results2DetailPaneProps): ReactElement => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const elementTypeLookup = useElementTypes()
  const invalidateInnerForcesEnvelopes = useInnerForcesEnvelopeInvalidation()
  const {
    selectedVerticalTransmitter: verticalTransmitter,
    setResultsVerticalTransmitter: setVerticalTransmitter,
  } = useResultsSelection()
  const detailTab = useEditorViewStore(state => state.results.detailTab)
  const setDetailTab = useEditorViewStore(state => state.setResultsDetailTab)
  const [checkSettingsTabKey, setCheckSettingsTabKey] = useState<string | null>(null)
  const getElementLabel = useElementLabel()
  const wallIdToAssembly = useWallIdToAssembly()

  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)

  // Linked Positionsplan comments: used to seed the "Plan" checkbox default and reconciled on submit.
  const comments = useCommentsStore(state => state.comments)
  const { reconcilePlanComment, planCommentNeedsReconcile } = useReconcilePlanComment()
  const syncLinkedPlanCommentText = useSyncLinkedPlanCommentText()

  // The selection stores the surface (Decke/Dach) guid. Its slab beam is the design
  // reference used for checks, cross section and the whole detailValues form below; the
  // surface guid itself only drives the merged surface tab and vertical transmission.
  const surface = useMemo(() => {
    if (!selectedGuid) return null
    const roof = find(verticalRoofSlabs, s => s.guid === selectedGuid)
    if (roof?.beam?.guid)
      return { guid: roof.guid, beamGuid: roof.beam.guid, label: 'Dach' as const }
    const slab = find(verticalSlabs, s => s.guid === selectedGuid)
    if (slab?.beam?.guid)
      return { guid: slab.guid, beamGuid: slab.beam.guid, label: 'Decke' as const }
    return null
  }, [selectedGuid, verticalRoofSlabs, verticalSlabs])

  // The reference guid used throughout the form: the slab beam for surface selections,
  // otherwise the selection itself (regular beams, purlins, columns, …).
  const elementId = surface?.beamGuid ?? selectedGuid

  // A slab/roof-slab beam carries the beam guid, but the transmission graph keys the slab's
  // supports by its surface guid. This map resolves a slab beam back to its surface guid so the
  // support lookups below find the connected (supporting) elements.
  const beamGuidToSlabGuid = useMemo(() => {
    const m = new Map<string, string>()
    for (const slab of verticalSlabs) {
      if (slab.beam?.guid) m.set(slab.beam.guid, slab.guid)
    }
    for (const slab of verticalRoofSlabs) {
      if (slab.beam?.guid) m.set(slab.beam.guid, slab.guid)
    }
    return m
  }, [verticalSlabs, verticalRoofSlabs])

  const setWallRipsPositionGrouping = useResultsStore(state => state.setWallRipsPositionGrouping)
  const setWallLintelsPositionGrouping = useResultsStore(
    state => state.setWallLintelsPositionGrouping,
  )
  const setSlabBeamsPositionGrouping = useResultsStore(state => state.setSlabBeamsPositionGrouping)
  const setRoofSlabBeamsPositionGrouping = useResultsStore(
    state => state.setRoofSlabBeamsPositionGrouping,
  )
  const setBeamsPositionGrouping = useResultsStore(state => state.setBeamsPositionGrouping)
  const setPurlinsPositionGrouping = useResultsStore(state => state.setPurlinsPositionGrouping)
  const setColumnsPositionGrouping = useResultsStore(state => state.setColumnsPositionGrouping)
  const setHighlightedCheck = useResultsStore(state => state.setHighlightedCheck)

  useEffect(() => {
    setHighlightedCheck(null)
  }, [elementId, setHighlightedCheck])

  const wallRipsPositionGrouping = useResultsStore(state => state.wallRipsPositionGrouping)
  const wallLintelsPositionGrouping = useResultsStore(state => state.wallLintelsPositionGrouping)
  const slabBeamsPositionGrouping = useResultsStore(state => state.slabBeamsPositionGrouping)
  const roofSlabBeamsPositionGrouping = useResultsStore(
    state => state.roofSlabBeamsPositionGrouping,
  )
  const beamsPositionGrouping = useResultsStore(state => state.beamsPositionGrouping)
  const purlinsPositionGrouping = useResultsStore(state => state.purlinsPositionGrouping)
  const columnsPositionGrouping = useResultsStore(state => state.columnsPositionGrouping)

  const positionBundlesFromStore = useMemo((): MemberPositionBundle[] | null => {
    switch (positionGroupingType) {
      case 'wall-rips':
        return wallRipsPositionGrouping
      case 'wall-lintels':
        return wallLintelsPositionGrouping
      case 'slab-beams':
        return slabBeamsPositionGrouping
      case 'roof-slab-beams':
        return roofSlabBeamsPositionGrouping
      case 'beams':
        return beamsPositionGrouping
      case 'purlins':
        return purlinsPositionGrouping
      case 'columns':
        return columnsPositionGrouping
      default:
        return null
    }
  }, [
    positionGroupingType,
    beamsPositionGrouping,
    columnsPositionGrouping,
    purlinsPositionGrouping,
    roofSlabBeamsPositionGrouping,
    slabBeamsPositionGrouping,
    wallLintelsPositionGrouping,
    wallRipsPositionGrouping,
  ])

  const calcElementChecksPending =
    useIsMutating(checkSettingsRecalculationMutationFilter(projectId, elementId)) > 0

  // Same data source MemberChecksTable (in Results) reads from. Filtering by
  // element_guid mirrors FormMemberChecksTable in CSAndAPPopupV2.
  const structuralChecks = useResultsStore(state => state.structuralChecks)
  const setStructuralChecks = useResultsStore(state => state.setStructuralChecks)
  const memberCheckSettings = useResultsStore(state => state.memberCheckSettings)
  const setSingleMemberCheckSetting = useResultsStore(state => state.setSingleMemberCheckSetting)
  const supportConfigsForChecks = useResultsStore(state => state.supportConfigsForChecks)
  const designForces = useResultsStore(state => state.designForces)
  const elementLoads = useResultsStore(state => state.elementLoads)
  const positionLoads = useResultsStore(state => state.positionLoads)
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)

  const designFormElementGuids = useMemo(() => {
    if (!elementId) return []
    return collectFokusGuidsForElement(verticalTransmissionGraph, elementId, beamGuidToSlabGuid)
  }, [elementId, verticalTransmissionGraph, beamGuidToSlabGuid])

  const domains = useAllDomains()
  const rips = useModelStore(state => state.model.rips)
  const lintels = useModelStore(state => state.model.lintels)
  const elementType = useElementType(elementId)

  // Fall back to Bemessung if the surface tab is active but this element has no slab surface.
  const tab: DetailPaneTabId = detailTab === 'surface' && !surface ? 'design' : detailTab
  const elementCrossSectionAssignment = useElementCrossSectionAssignment()
  const slabBeamGuidToAssembly = useSlabBeamToAssembly()
  const { data: documentsData, isLoading: isLoadingDocuments } =
    useProjectFiles(projectFilesRootGuid)
  const defaultValues = useMemo((): DesignFormRootValues => {
    if (!elementId) {
      return { elements: [], supportConfigIndexToElementIndex: {} }
    }

    const elements = designFormElementGuids.map(
      (guid, index): DesignFormRootValues['elements'][number] => {
        const commentTexts = commentTextsForPositionGroupingBundle(positionBundlesFromStore, guid)
        const exportedVal =
          index === 0
            ? accepted ?? false
            : exportedFlagForPositionGroupingRepresentativeOnly(positionBundlesFromStore, guid)

        return buildDefaultDesignFormValues({
          elementGuid: guid,
          memberCheckSettingsSelectedElement:
            structuralChecks && structuralChecks.length > 0
              ? find(memberCheckSettings, { element_guid: guid }) ?? null
              : null,
          exported: exportedVal,
          crossSectionFromAssignment: resolveResults2DetailPaneCrossSection({
            elementId: guid,
            elementType: elementTypeLookup[guid],
            elementCrossSectionAssignment,
            slabBeamGuidToAssembly,
          }),
          comment: commentTexts.comment,
          extendedComment: commentTexts.extendedComment,
          notes: commentTexts.notes,
          showInPositionPlan: comments.some(
            comment => comment.element_guid === guid && getCommentType(comment) === 'position-plan',
          ),
          // Checks enter the form here only; SupportsGrid and submit read `elements.*.checksSelectedElement`.
          checksSelectedElement: filter(structuralChecks ?? [], { element_guid: guid }),
          supportConfigsForChecks: index === 0 ? supportConfigsForChecks : [],
          supportsOfElement: getSupportsOfElement(
            beamGuidToSlabGuid.get(guid) ?? guid,
            verticalTransmissionGraph,
          ),
        })
      },
    )

    const supportsOfRoot = elements[0].supportsOfElement

    const memberCheckSettingsSelectedElement = elements[0].memberCheckSettingsSelectedElement

    const supportConfigs =
      memberCheckSettingsSelectedElement && 'support_configs' in memberCheckSettingsSelectedElement
        ? memberCheckSettingsSelectedElement.support_configs ?? []
        : []

    const supportConfigIndexToElementIndex = supportConfigs.reduce((acc, config, index) => {
      const support = supportsOfRoot.find(({ relativePosition }) =>
        matchesRelativePosition(relativePosition, config.relative_position),
      )

      const elementIndex = findIndex(elements, element => element.elementGuid === support?.guid)

      return {
        ...acc,
        [index]: elementIndex,
      }
    }, {})

    return { elements, supportConfigIndexToElementIndex }
  }, [
    accepted,
    beamGuidToSlabGuid,
    comments,
    designFormElementGuids,
    elementCrossSectionAssignment,
    elementId,
    elementTypeLookup,
    memberCheckSettings,
    positionBundlesFromStore,
    slabBeamGuidToAssembly,
    structuralChecks,
    supportConfigsForChecks,
    verticalTransmissionGraph,
  ])

  const selectedElementDesignForces = useMemo(
    () => find(designForces, ['element_guid', elementId]),
    [designForces, elementId],
  )

  const loads = useMemo(
    () => [...(elementLoads || []), ...(positionLoads || [])],
    [elementLoads, positionLoads],
  )

  const allPositions = useMemo(
    () => (rips && lintels ? [...rips, ...lintels] : []),
    [rips, lintels],
  )

  const slabBeams = useMemo(
    () =>
      verticalSlabs && verticalRoofSlabs
        ? [...verticalSlabs, ...verticalRoofSlabs].map(slab => slab.beam)
        : [],
    [verticalSlabs, verticalRoofSlabs],
  )

  const allLoadsWithDomain = useEnhanceLoadsWithDomain({
    loads,
    domains,
    verticalTransmissionGraph,
    slabBeams,
  })

  const guidToPosition = useMemo(() => mapValueKey(allPositions, 'position_guid'), [allPositions])
  const slabBeamGuidToBeam = useMemo(() => mapValueKey(slabBeams), [slabBeams])

  const filteredLoads = useMemo(() => {
    if (!elementId) return null
    const position = guidToPosition[elementId] || slabBeamGuidToBeam[elementId]
    const currentElementGuid =
      position && position.position_guid ? position.position_guid : elementId
    return filter(allLoadsWithDomain || [], load => load.element_guid === currentElementGuid) as (
      | AreaLoad
      | DomainLoad
    )[]
  }, [allLoadsWithDomain, elementId, guidToPosition, slabBeamGuidToBeam])

  // Surface (vertical slab) loads for the Dach/Decke tab: only loads on the slab itself,
  // mirroring Results2AssemblySurfaceDetailPane (slab-beam loads stay on the Bemessung tab).
  const surfaceLoads = useMemo(() => {
    if (!surface) return [] as (AreaLoad | DomainLoad)[]
    return filter(allLoadsWithDomain || [], load => load.element_guid === surface.guid) as (
      | AreaLoad
      | DomainLoad
    )[]
  }, [allLoadsWithDomain, surface])

  const documents = useMemo(
    () => (elementId ? getElementDocuments(documentsData.files, elementId) : []),
    [documentsData.files, elementId],
  )

  const defaultLoadTab = useResults2DefaultLoadTab(elementId)
  const surfaceDefaultLoadTab = useResults2DefaultLoadTab(surface?.guid)

  const { mutate: calcPositionChecks, isLoading: isCalcPositionChecksLoading } = useMutation({
    mutationFn: async (beamLike: string[]) => {
      await postCalcPositionChecks.request(projectId, beamLike)
    },
    // the recomputed member results (e.g. for a new cross section) change the envelopes
    onSuccess: () => invalidateInnerForcesEnvelopes(projectId as string),
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Neuberechnen der Nachweise'), {
        variant: 'error',
      }),
  })

  const { mutate: onSubmit, isLoading: isSubmitting } = useMutation({
    mutationKey: ['saveDetailForm', elementId],
    mutationFn: async (state: DesignFormRootValues) => {
      const slice =
        (elementId ? state.elements.find(e => e.elementGuid === elementId) : undefined) ??
        state.elements[0]

      if (!projectId || !elementId || !slice) throw new Error('Invalid state')

      const groupingForSave = positionBundlesFromStore ?? positionGrouping ?? []
      const wantsPositionGroupingPersist =
        Boolean(groupingForSave.length) && Boolean(positionGroupingType)
      const hasMemberCheckSettings = Boolean(slice.memberCheckSettingsSelectedElement)

      const crossSectionsToAssign: CrossSectionOnElement[] = []
      for (const el of state.elements) {
        const guid = el.elementGuid
        if (!guid || el.crossSection == null) continue
        const et = elementTypeLookup[guid]
        if (et === 'slab_beams' || et === 'roof_slab_beams') continue
        crossSectionsToAssign.push({
          ...hydrateCrossSectionForCalcElementChecksRequest(el.crossSection),
          element_guid: guid,
        } as CrossSectionOnElement)
      }

      // The "Plan" checkbox (elements.0) is a form field now; its linked comment is created/deleted
      // when the Bemessung is saved. A lone Plan change is a valid reason to save. The reconcile
      // itself runs in onSuccess (not here) so its comments-store write can't remount the form
      // mid-save — see the note there.
      const desiredShowInPositionPlan = !!slice.comments.showInPositionPlan
      const planNeedsReconcile = planCommentNeedsReconcile(
        elementId,
        desiredShowInPositionPlan,
        slice.comments.positionList,
      )

      if (
        !hasMemberCheckSettings &&
        !wantsPositionGroupingPersist &&
        crossSectionsToAssign.length === 0 &&
        !planNeedsReconcile
      ) {
        throw new Error('Invalid state')
      }

      // Stamp the tolerances with the design context they are saved against
      // (cross section, support layout, supporting elements) so stale values
      // can be detected — and drop the ones that are already stale.
      const requestSettings = slice.memberCheckSettingsSelectedElement
        ? withMemberCheckSettingsReferenceData(slice.memberCheckSettingsSelectedElement, {
            crossSection: slice.crossSection,
            supports: buildMemberCheckSettingSupportsContext(elementId, verticalTransmissionGraph),
          })
        : slice.memberCheckSettingsSelectedElement

      const nextStructuralChecks: CombinedPositionCheck[] = hasMemberCheckSettings
        ? [
            ...reject(structuralChecks ?? [], { element_guid: elementId }),
            ...slice.checksSelectedElement,
          ]
        : [...(structuralChecks ?? [])]

      const positionGroupingCommentsSave =
        wantsPositionGroupingPersist && positionGroupingType
          ? {
              grouping: groupingForSave,
              type: positionGroupingType,
              comment: slice.comments.positionList,
              extended_comment: slice.comments.preliminaryRemarks,
              notes: slice.comments?.notes ?? '',
              exported: resolveExportedForPositionGroupingSave(
                elementId,
                groupingForSave,
                slice.exported,
              ),
            }
          : undefined

      if (crossSectionsToAssign.length > 0) {
        await saveCrossSectionsOnElement.request(projectId, crossSectionsToAssign)
      }

      let savedSettings: SettingsOnMember | undefined
      if (requestSettings) {
        savedSettings = await saveSingleMemberCheckSettings.request(projectId, requestSettings)
      }

      // The saved grouping is mirrored into its store in onSuccess (not here) for the same reason as
      // the linked comment: writing it mid-save would remount the form before the rest has saved.
      let savedGrouping: { type: PositionGroupingType; data: MemberPositionBundle[] } | undefined
      if (positionGroupingCommentsSave) {
        const {
          grouping,
          type,
          comment: bundleComment,
          extended_comment,
          notes: bundleNotes,
          exported: bundleExported,
        } = positionGroupingCommentsSave
        const bundle = grouping.find(
          b =>
            b.representative_position === elementId ||
            (b.representative_for ?? []).includes(elementId),
        )
        if (bundle) {
          const updated = grouping.map(b =>
            b.representative_position === bundle.representative_position
              ? {
                  ...b,
                  comment: bundleComment,
                  extended_comment,
                  notes: bundleNotes,
                  exported: bundleExported,
                }
              : b,
          )
          const data = await savePositionGrouping.request(projectId, type, updated)
          savedGrouping = { type, data }
        }
      }

      const beamLike = beamLikeGuidsForCalcPositionChecks({
        elementId,
        verticalTransmissionGraph,
        elementTypeLookup,
      })
      if (beamLike.length > 0) {
        calcPositionChecks(beamLike)
      }

      return {
        savedSettings,
        nextStructuralChecks,
        crossSectionsToAssign,
        savedGrouping,
        planComment: {
          elementId,
          planNeedsReconcile,
          desiredShowInPositionPlan,
          positionList: slice.comments.positionList,
        },
      }
    },
    onSuccess: ({
      savedSettings,
      nextStructuralChecks,
      crossSectionsToAssign,
      savedGrouping,
      planComment,
    }) => {
      if (crossSectionsToAssign.length > 0) {
        void queryClient.invalidateQueries(getModel.getKey(projectId))
      }

      if (savedSettings) {
        setSingleMemberCheckSetting(savedSettings)
      }
      setStructuralChecks(nextStructuralChecks)

      if (savedGrouping) {
        const { type, data } = savedGrouping
        if (type === 'wall-rips') setWallRipsPositionGrouping(data)
        else if (type === 'wall-lintels') setWallLintelsPositionGrouping(data)
        else if (type === 'slab-beams') setSlabBeamsPositionGrouping(data)
        else if (type === 'roof-slab-beams') setRoofSlabBeamsPositionGrouping(data)
        else if (type === 'beams') setBeamsPositionGrouping(data)
        else if (type === 'purlins') setPurlinsPositionGrouping(data)
        else if (type === 'columns') setColumnsPositionGrouping(data)
      }

      // Reflect the linked Positionsplan comment only now that the Bemessung has saved, so every
      // store the form's defaultValues read from updates together. Done inside mutationFn, these
      // writes hit the comments store mid-save and remounted the form, briefly showing the
      // not-yet-saved fields at their pre-edit values. Fire-and-forget: the comment mutations
      // surface their own errors and the main save has already succeeded.
      if (planComment.planNeedsReconcile) {
        void reconcilePlanComment(
          planComment.elementId,
          planComment.desiredShowInPositionPlan,
          planComment.positionList,
        ).catch(() => undefined)
      }
      syncLinkedPlanCommentText(planComment.elementId, planComment.positionList)

      enqueueSnackbar('Bemessung gespeichert', {
        variant: 'success',
        autoHideDuration: 2500,
        preventDuplicate: true,
      })
    },
    onError: (error: Error) => {
      void queryClient.invalidateQueries(getModel.getKey(projectId))
      enqueueSnackbar(
        buildErrorMessage(error as AxiosError, 'Fehler beim Speichern der Bemessung'),
        { variant: 'error' },
      )
    },
  })

  const key = useMemo(() => JSON.stringify(defaultValues), [defaultValues])
  const commentsSection = useResultsSectionOpen('results2.design.comments')
  const checkSettingsSection = useResultsSectionOpen('results2.design.check-settings')
  const supportsSection = useResultsSectionOpen('results2.design.supports')
  const designLoadsSection = useResultsSectionOpen('results2.design.loads')
  const surfaceLoadsSection = useResultsSectionOpen('results2.surface.loads')
  return (
    <Form2
      // remount the form (and reset all values) when the selected element changes
      key={key}
      defaultValues={defaultValues}
      mode="onChange"
      validationSchema={results2DesignFormValidationSchema}
      resetIsDirtyAfterSubmit
      onSubmit={async (data: DesignFormRootValues) => onSubmit(data)}
      style={{
        height: '100%',
        minHeight: 0,
        background: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <DesignFormDirtyBlocker />
      <GuardedResults2DetailTabs
        tab={tab}
        setTab={setDetailTab}
        documentCount={documents.length}
        surfaceTabLabel={surface?.label}
      />

      {tab === 'design' ? (
        <DetailPaneExportedToggle
          elementLabel={elementLabel}
          elementId={elementId}
          elementSubtitle={elementSubtitle}
          calcElementChecksPending={calcElementChecksPending}
          disableForm={disableForm}
          isChildElement={isChildElement}
        />
      ) : null}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {tab === 'design' ? (
          <Stack spacing={3} sx={{ px: 2, py: 1.5 }}>
            <Section title="Kommentare" collapsible hideBorderBottom {...commentsSection}>
              <CommentsSection disableForm={disableForm} isChildElement={isChildElement} />
            </Section>

            <MaterialSection
              elementGuid={elementId}
              disableAllFields={elementType === 'slab_beams' || elementType === 'roof_slab_beams'}
              disableForm={disableForm}
            />

            {elementType && ELEMENT_TYPES_WITH_INNER_FORCES_PLOT.includes(elementType) ? (
              <InnerForcesSection elementId={elementId} />
            ) : null}

            <MemberChecksSection
              activeSettingsTab={checkSettingsTabKey}
              elementGuid={elementId}
              disableForm={disableForm}
            />

            <Section
              title="Nachweiseinstellungen"
              collapsible
              hideBorderBottom
              {...checkSettingsSection}
            >
              {elementType ? (
                <Results2CheckSettingsTabs
                  elementType={elementType}
                  supportsGrid={<SupportsGrid elementId={elementId} disableForm={disableForm} />}
                  onActiveTabKeyChange={setCheckSettingsTabKey}
                />
              ) : (
                <Box sx={{ pt: 1, color: 'text.secondary', fontSize: 12 }}>—</Box>
              )}
            </Section>

            <Section title="Auflager" collapsible hideBorderBottom {...supportsSection}>
              <FooterTable
                elementId={elementId}
                designForces={selectedElementDesignForces?.design_support_forces ?? []}
                designForcesWithoutKMods={
                  selectedElementDesignForces?.design_support_forces_without_kmods ?? []
                }
              />
            </Section>

            <CharacteristicSupportReactionsSection elementId={elementId} />

            <Section title="Lasten" collapsible hideBorderBottom {...designLoadsSection}>
              <LastenTable
                loads={filteredLoads}
                defaultLoadTab={defaultLoadTab}
                elementId={elementId}
              />
            </Section>
          </Stack>
        ) : tab === 'surface' && surface ? (
          <>
            <Results2PaneHeader
              title={getElementLabel(surface.guid) || surface.guid}
              subtitle={[surface.label, wallIdToAssembly[surface.guid]?.name]
                .filter(Boolean)
                .join(' · ')}
            />
            <Stack spacing={3} sx={{ px: 2, py: 1.5 }}>
              <SystemSection
                surfaceGuid={surface.guid}
                beamGuid={surface.beamGuid}
                elementType={surface.label === 'Dach' ? 'roof_slab_beams' : 'slab_beams'}
                disableForm={disableForm}
              />
              <Section title="Lasten" collapsible hideBorderBottom {...surfaceLoadsSection}>
                <Results2LoadResults
                  loads={surfaceLoads}
                  defaultLoadTab={surfaceDefaultLoadTab}
                  elementId={surface.guid}
                />
              </Section>
            </Stack>
          </>
        ) : tab === 'documents' ? (
          <DocumentsTab
            elementId={elementId}
            documents={documents}
            isLoadingDocuments={isLoadingDocuments}
            disableForm={disableForm}
          />
        ) : elementId ? (
          <Box sx={{ p: 2 }}>
            <VerticalTransmitterList
              // Show the surface's configured vertical transmission, not the slab beam's.
              selectedElement={selectedGuid}
              verticalTransmitter={verticalTransmitter}
              setVerticalTransmitter={guid => setVerticalTransmitter(guid)}
              disableChanging
            />
          </Box>
        ) : (
          <EmptyState sx={{ py: 2 }}>Kein Bauteil ausgewählt</EmptyState>
        )}
      </Box>

      {tab === 'design' ? (
        <BottomActionBar undoBaselineValues={defaultValues} disableForm={disableForm} />
      ) : null}
      <CheckSettingsRecalculation
        elementId={elementId}
        enabled={
          tab === 'design' &&
          !isSubmitting &&
          !disableForm &&
          !isCalcPositionChecksLoading &&
          (structuralChecks || []).length > 0
        }
      />
      <SyncCrossSectionWithScene disabled={disableForm} />
    </Form2>
  )
}
export {
  RESULTS2_DESIGN_FORM_BLOCK_ACTION,
  UNSAVED_BEMESSUNG_NAV_MESSAGE,
  unsavedBemessungNavSnackbarOptions,
} from './constants'
export { buildMemberCheckRows } from './utils'
export { DocumentsTab } from './components/DocumentsTab'
export { MemberChecksSection } from './components/MemberChecksSection'
export default Results2DetailPane
